import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, BookOpen, ExternalLink } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const About = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  return (
    <div className="min-h-screen py-12 px-6 relative z-10">
      <div className="max-w-3xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors mb-8 font-sans"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'tr' ? 'Geri' : 'Back'}
        </button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-serif font-light text-4xl md:text-5xl text-[#F8F5F2] mb-4">
            {language === 'tr' ? 'IN Hakkında' : 'About IN'}
          </h1>
        </motion.div>

        {/* What is IN */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-panel rounded-2xl p-8 mb-8"
        >
          <h2 className="font-serif font-light text-2xl text-[#D4AF37] mb-4">
            {language === 'tr' ? 'IN Nedir?' : 'What is IN?'}
          </h2>
          <div className="space-y-4 font-sans text-[#E5E5E5] leading-relaxed">
            <p>
              <strong className="text-[#F8F5F2]">IN — Inner Navigation</strong> {language === 'tr' 
                ? 'bir hayat navigasyon sistemidir.'
                : 'is a life navigation system.'}
            </p>
            <p>
              {language === 'tr'
                ? 'Çoğu uygulama tek bir şeyi izler: fitness, finans, alışkanlıklar veya zaman. IN ise yörüngeyi izler — hayatınızın en önemli alanlarda aslında nereye gittiğini.'
                : "Most apps track one thing: fitness, finance, habits, or time. IN tracks trajectory — where your life is actually going across the domains that matter most."}
            </p>
            <p>
              {language === 'tr'
                ? '12 alanlı değerlendirme, yapay zeka destekli rehberlik ve haftalık check-in\'ler kullanarak IN, hayatınızı net görmenize, neyin ilgi gerektirdiğini belirlemenize ve odaklı aksiyon almanıza yardımcı olur.'
                : 'Using a 12-domain assessment, AI-powered guidance, and weekly check-ins, IN helps you see your life clearly, identify what needs attention, and take focused action.'}
            </p>
            <p>
              {language === 'tr'
                ? 'Sonuç, sizinle birlikte gelişen hayatınızın canlı bir görüntüsü olan Güneş Haritanızdır.'
                : 'The result is your Sun Map — a living snapshot of your life that evolves as you do.'}
            </p>
          </div>
        </motion.section>

        {/* The Philosophy */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-panel rounded-2xl p-8 mb-8"
        >
          <h2 className="font-serif font-light text-2xl text-[#D4AF37] mb-4">
            {language === 'tr' ? 'Felsefe' : 'The Philosophy'}
          </h2>
          <div className="space-y-4 font-sans text-[#E5E5E5] leading-relaxed">
            <p>
              {language === 'tr'
                ? 'IN basit bir önerme üzerine kurulmuştur: göremediğiniz şeyde yol alamazsınız.'
                : "IN is built on a simple premise: you can't navigate what you can't see."}
            </p>
            <p>
              {language === 'tr'
                ? 'Sıklıkla sıkışmış, dağınık veya nereye odaklanacağımızdan emin olamayız — motivasyon eksikliğinden değil, bir haritamız olmadığı için. IN size o haritayı verir, sonra hareket etmenize yardımcı olur.'
                : "We often feel stuck, scattered, or unsure where to focus — not because we lack motivation, but because we lack a map. IN gives you that map, then helps you move."}
            </p>
            <p>
              {language === 'tr'
                ? 'Jung psikolojisi, anlatı kimliği ve davranış biliminden ilham alan IN, kişisel gelişimi kişisel gelişim tiyatrosu olarak değil, gerçek navigasyon olarak ele alır — bir varış noktası, mevcut bir konum ve ileriye dönük bir yol ile.'
                : 'Inspired by Jungian psychology, narrative identity, and behavioral science, IN treats personal development not as self-improvement theater, but as real navigation — with a destination, a current position, and a path forward.'}
            </p>
          </div>
        </motion.section>

        {/* Who Built This */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-panel rounded-2xl p-8 mb-8"
        >
          <h2 className="font-serif font-light text-2xl text-[#D4AF37] mb-6">
            {language === 'tr' ? 'Bunu Kim Yaptı?' : 'Who Built This?'}
          </h2>
          
          <div className="flex flex-col md:flex-row gap-6 items-start">
            {/* Photo */}
            <div className="flex-shrink-0">
              <img
                src="https://customer-assets.emergentagent.com/job_life-map-coach/artifacts/5didx69a_34d669be-188f-4623-bc9b-2cbca014e20c%202.jpg"
                alt="Ray Rizzo"
                className="w-32 h-32 md:w-40 md:h-40 rounded-2xl object-cover border-2 border-[#D4AF37]/30"
              />
            </div>
            
            {/* Bio */}
            <div className="space-y-4 font-sans text-[#E5E5E5] leading-relaxed">
              <p>
                <strong className="text-[#F8F5F2]">Ray Rizzo</strong> {language === 'tr'
                  ? 'insan gelişimi ve davranış değişikliğine odaklanan bir yazar, sistem düşünürü ve sağlık danışmanıdır.'
                  : 'is a writer, systems thinker, and health consultant focused on human development and behavioral change.'}
              </p>
              <p>
                {language === 'tr'
                  ? 'Bütünleşik erkekliği ve modern geçiş ritlerini keşfeden bir kitap olan'
                  : 'He is the author of'} <em className="text-[#D4AF37]">{language === 'tr' ? 'Güneş Kral & Erkekliğe Yeni Bir Bakış' : 'Sun King: Masculinity Beyond Reaction and Retreat'}</em>{language === 'tr' ? '\'ın yazarıdır.' : ', a book exploring integrated masculinity and modern rites of passage.'}
              </p>
              <p>
                {language === 'tr'
                  ? 'IN aracılığıyla Ray, insanların hayatlarını haritalandırmalarına ve sağlık, ilişkiler, amaç ve iş alanlarında ölçülebilir ilerleme kaydetmelerine yardımcı olmak için psikoloji, anlatı kimliği ve yapay zeka rehberliğini birleştiren ölçeklenebilir bir platform oluşturuyor.'
                  : 'Through IN, Ray is building a scalable platform that combines psychology, narrative identity, and AI guidance to help people map their lives and make measurable progress across health, relationships, purpose, and work.'}
              </p>
            </div>
          </div>
        </motion.section>

        {/* Contact & Links */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <a
            href="mailto:rayrizz@gmail.com"
            className="flex items-center gap-2 px-6 py-3 rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#030305] transition-all duration-300 font-sans"
            data-testid="contact-email"
          >
            <Mail className="w-4 h-4" />
            {language === 'tr' ? 'İletişim' : 'Contact Ray'}
          </a>
          
          <button
            onClick={() => navigate('/privacy')}
            className="flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-[#A1A1AA] hover:text-[#F8F5F2] hover:border-white/40 transition-all duration-300 font-sans"
            data-testid="privacy-link"
          >
            <BookOpen className="w-4 h-4" />
            {language === 'tr' ? 'Gizlilik Politikası' : 'Privacy Policy'}
          </button>
        </motion.section>

      </div>
    </div>
  );
};

export default About;
